import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, TrendingUp, Crown, Check, ShieldCheck, Star } from 'lucide-react';
import CourseQuiz from '@/components/CourseQuiz';

const LEVELS = [
  {
    level: '01',
    tag: 'Level 1 — Foundations',
    title: 'Beginner',
    href: '/courses',
    icon: BookOpen,
    accent: 'emerald',
    forWho: 'For traders who are new to forex or want a structured restart.',
    courses: ['Forex Made Simple'],
    fromPrice: 97,
    bullets: [
      'Read charts & candle patterns',
      'Set up MT4 / MT5 from scratch',
      'Place your first confident trade',
    ],
    cta: 'Start Here',
  },
  {
    level: '02',
    tag: 'Level 2 — Build Your Edge',
    title: 'Intermediate',
    href: '/intermediate',
    icon: TrendingUp,
    accent: 'amber',
    forWho: 'For traders ready to automate day trading and run a complete scalping system in parallel.',
    courses: ['9-Step Pro System', 'Bow-Tie Scalping System'],
    fromPrice: 297,
    bullets: [
      'Full 9-Step day trading automation',
      'Bow-Tie scalping signal pattern',
      'Multi-timeframe confirmation (H1/M15/M5)',
    ],
    cta: 'Build Your Edge',
  },
  {
    level: '03',
    tag: 'Level 3 — Master',
    title: 'Advanced',
    href: '/advanced',
    icon: Crown,
    accent: 'violet',
    forWho: 'For serious traders who want swing & position trading, plus the all-in-one Pro Trader Bundle.',
    courses: ['Swing Trading', 'Position Trading', 'Pro Trader Bundle (all 4 courses)'],
    fromPrice: 247,
    bullets: [
      'Longer-horizon swing & position systems',
      'Pro Trader Bundle — all 4 courses',
      'Lifetime license for everything',
    ],
    cta: 'Go Pro',
  },
];

const ACCENT_STYLES = {
  emerald: {
    border: 'hover:border-emerald-400/40',
    ring: 'group-hover:ring-emerald-400/20',
    text: 'text-emerald-400',
    bg: 'bg-emerald-400/10',
    btn: 'bg-emerald-400 text-black hover:bg-emerald-300',
    badge: 'bg-emerald-400/10 text-emerald-300 border-emerald-400/20',
  },
  amber: {
    border: 'hover:border-amber-400/40',
    ring: 'group-hover:ring-amber-400/20',
    text: 'text-amber-400',
    bg: 'bg-amber-400/10',
    btn: 'bg-amber-400 text-black hover:bg-amber-300',
    badge: 'bg-amber-400/10 text-amber-300 border-amber-400/20',
  },
  violet: {
    border: 'hover:border-violet-400/40',
    ring: 'group-hover:ring-violet-400/20',
    text: 'text-violet-400',
    bg: 'bg-violet-400/10',
    btn: 'bg-violet-400 text-black hover:bg-violet-300',
    badge: 'bg-violet-400/10 text-violet-300 border-violet-400/20',
  },
};

export default function AllCoursesPage() {
  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="all-courses-page">
      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 pb-12 text-center relative overflow-hidden" data-testid="all-courses-hero">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-amber-400/[0.04] blur-[160px]" />
        <div className="relative mx-auto max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-400/80 mb-4">All Courses</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
            Your <span className="text-amber-400">Trading Journey</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/50 max-w-2xl mx-auto leading-relaxed">
            Three levels. One clear path. Start where you are — from your first chart to a fully automated trading suite.
          </p>
        </div>
      </section>

      {/* Journey strip */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16" data-testid="journey-strip">
        <div className="mx-auto max-w-4xl flex items-center justify-center gap-3 sm:gap-6 text-xs sm:text-sm font-semibold tracking-wider uppercase">
          <span className="text-emerald-400">Beginner</span>
          <ArrowRight size={16} className="text-white/30" />
          <span className="text-amber-400">Intermediate</span>
          <ArrowRight size={16} className="text-white/30" />
          <span className="text-violet-400">Advanced</span>
        </div>
      </section>

      {/* 3 Level Cards */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20" data-testid="levels-grid">
        <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-3 gap-6">
          {LEVELS.map((lvl) => {
            const Icon = lvl.icon;
            const a = ACCENT_STYLES[lvl.accent];
            return (
              <Link
                key={lvl.level}
                to={lvl.href}
                className={`group relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7 flex flex-col transition-all duration-300 hover:bg-white/[0.04] ${a.border} hover:-translate-y-1 ring-1 ring-transparent ${a.ring}`}
                data-testid={`level-card-${lvl.title.toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className={`w-11 h-11 rounded-xl ${a.bg} flex items-center justify-center`}>
                    <Icon size={20} className={a.text} />
                  </div>
                  <span className={`text-xs font-mono ${a.text}/70`}>{lvl.level}</span>
                </div>

                <p className={`text-[10px] font-bold uppercase tracking-[0.18em] ${a.text} mb-2`}>{lvl.tag}</p>
                <h2 className="text-3xl font-bold text-white tracking-tight">{lvl.title}</h2>
                <p className="mt-3 text-sm text-white/50 leading-relaxed">{lvl.forWho}</p>

                <div className="mt-6 pt-6 border-t border-white/[0.06]">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-white/40 mb-3">Includes</p>
                  <ul className="space-y-2">
                    {lvl.courses.map((c) => (
                      <li key={c} className="flex items-center gap-2 text-sm text-white/80">
                        <span className={`w-1.5 h-1.5 rounded-full ${a.bg.replace('/10', '')}`} />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>

                <ul className="mt-6 space-y-2 flex-1">
                  {lvl.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-xs text-white/50">
                      <Check size={13} className={`${a.text} mt-0.5 shrink-0`} />
                      {b}
                    </li>
                  ))}
                </ul>

                <div className="mt-7 flex items-end justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-white/30">{lvl.fromPrice ? 'From' : 'Pricing'}</p>
                    <p className="text-2xl font-bold text-white">{lvl.fromPrice ? `$${lvl.fromPrice}` : 'TBA'}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold ${a.btn} transition-colors`}>
                    {lvl.cta} <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Recommended for you — Interactive Quiz */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20" data-testid="course-quiz-section">
        <div className="mx-auto max-w-3xl">
          <CourseQuiz />
        </div>
      </section>

      {/* Trust strip */}
      <section className="px-4 sm:px-6 lg:px-8 pb-12" data-testid="trust-strip">
        <div className="mx-auto max-w-5xl grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="flex justify-center gap-0.5 mb-2">{[...Array(5)].map((_, i) => <Star key={i} size={13} className="fill-amber-400 text-amber-400" />)}</div>
            <p className="text-xs text-white/50">4.9/5 Forex Peace Army</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-white">20+</p>
            <p className="text-xs text-white/50">Years Trading</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-white">900+</p>
            <p className="text-xs text-white/50">Students Trained</p>
          </div>
          <div>
            <ShieldCheck size={22} className="text-emerald-400 mx-auto mb-1" />
            <p className="text-xs text-white/50">30-Day Money Back</p>
          </div>
        </div>
      </section>
    </div>
  );
}
